import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { authService, restaurantService, billingService, posService, crmService } from './services/supabase';
import { startSync, stopSync, hasPendingSync } from './services/syncService';
import AuthRouter from './components/auth/AuthRouter';
import WaiterDashboard from './components/WaiterDashboard';
import OwnerApp from './components/OwnerApp';
import ManagerPortalApp from './components/manager/ManagerApp';
import PublicMenu from './components/PublicMenu';
import PublicInvoicePage from './components/pos/PublicInvoicePage';
import ResetPassword from './components/auth/ResetPassword';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  useEffect(() => {
    checkExistingSession();
  }, []);

  const syncToLocalDb = async (restaurant) => {
    if (!window.menuronDesktop?.isDesktop || !restaurant?.id) {
      console.log('[sync] skipped — not desktop or no restaurant id', restaurant?.id);
      return;
    }
    console.log('[sync] starting for restaurant', restaurant.id);
    try {
      // Use allSettled so one failing fetch doesn't block the others from seeding
      const [menuRes, tablesRes, billingRes, ordersRes, customersRes, managersRes, waitersRes] =
        (await Promise.allSettled([
          restaurantService.getMenu(restaurant.id),
          restaurantService.getTables(restaurant.id),
          billingService.getSettings(restaurant.id),
          posService.getCounterOrders(restaurant.id, { days: 30 }),
          crmService.getCustomers(restaurant.id),
          authService.getRestaurantManagers(restaurant.id),
          authService.getRestaurantWaiters(restaurant.id),
        ])).map(r => r.status === 'fulfilled' ? r.value : { data: null, error: r.reason });

      console.log('[sync] fetched from Supabase — cats:', menuRes.data?.length, 'tables:', tablesRes.data?.length, 'orders:', ordersRes.data?.length, 'managers:', managersRes.data?.length, 'waiters:', waitersRes.data?.length);
      const result = await window.menuronDesktop.local.seedData({
        restaurant,
        categories: menuRes.data || [],
        tables: tablesRes.data || [],
        billingSettings: billingRes.data || null,
        orders: ordersRes.data || [],
        customers: customersRes.data || [],
        managers: managersRes.data || [],
        waiters: waitersRes.data || [],
      });
      console.log('[sync] seedData result:', JSON.stringify(result));
    } catch (e) {
      console.error('[sync] FAILED:', e.message, e.stack);
    }
  };

  const checkExistingSession = async () => {
    const sessionToken = localStorage.getItem('menuron_session');

    if (sessionToken) {
      // Verify session with Supabase
      const { data, error } = await authService.verifySession(sessionToken);

      if (data && !error) {
        // Cache session locally so offline restarts work without hitting Supabase
        if (window.menuronDesktop?.isDesktop) {
          localStorage.setItem('menuron_local_session', JSON.stringify({
            user: data.user, restaurant: data.restaurant, userType: data.userType,
          }));
        }
        syncToLocalDb(data.restaurant);  // non-blocking
        if (window.menuronDesktop?.isDesktop) {
          startSync(data.restaurant.id);
        }
        setUser(data.user);
        setRestaurant(data.restaurant);
        setUserType(data.userType);
      } else if (window.menuronDesktop?.isDesktop) {
        // Offline: fall back to cached session
        const cached = localStorage.getItem('menuron_local_session');
        if (cached) {
          try {
            const { user, restaurant, userType } = JSON.parse(cached);
            startSync(restaurant.id);
            setUser(user);
            setRestaurant(restaurant);
            setUserType(userType);
            console.log('[session] offline — using cached local session for', restaurant.name);
          } catch {
            await authService.logout();
          }
        } else {
          await authService.logout();
        }
      } else {
        await authService.logout();
      }
    }

    setLoading(false);
  };

  const handleAuthSuccess = async (authData) => {
    if (window.menuronDesktop?.isDesktop) {
      localStorage.setItem('menuron_local_session', JSON.stringify({
        user: authData.user, restaurant: authData.restaurant, userType: authData.userType,
      }));
      syncToLocalDb(authData.restaurant);
      startSync(authData.restaurant.id);
    } else {
      syncToLocalDb(authData.restaurant);
    }
    setUser(authData.user);
    setRestaurant(authData.restaurant);
    setUserType(authData.userType);
    if (authData.userType === 'owner') navigate('/analytics');
    else if (authData.userType === 'manager') navigate('/manager-dashboard');
    else navigate('/');
  };

  const [syncBlockMessage, setSyncBlockMessage] = useState('');

  const handleLogout = async () => {
    if (window.menuronDesktop?.isDesktop) {
      const pending = await hasPendingSync(restaurant?.id);
      if (pending) {
        setSyncBlockMessage('Your data is not yet synced to the cloud. Please wait for the internet connection to restore and sync to complete before logging out.');
        setTimeout(() => setSyncBlockMessage(''), 6000);
        return;
      }
      stopSync();
      localStorage.removeItem('menuron_local_session');
    }
    await authService.logout();
    setUser(null);
    setRestaurant(null);
    setUserType(null);
    navigate('/');
  };


  // Loading screen
  const LoadingScreen = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
    }}>
      <div style={{
        background: 'white',
        padding: '40px',
        borderRadius: '20px',
        textAlign: 'center',
        boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #FF4444',
          borderTop: '4px solid transparent',
          borderRadius: '50%',
          margin: '0 auto 20px',
          animation: 'spin 1s linear infinite'
        }} />
        <h2 style={{ color: '#333', margin: '0' }}>Loading Menuron...</h2>
      </div>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );

  // Main authenticated content
  const AuthenticatedContent = () => {
    if (!user) {
      return <AuthRouter onAuthSuccess={handleAuthSuccess} />;
    }

    if (userType === 'owner') {
      return <OwnerApp restaurant={restaurant} user={user} onLogout={handleLogout} />;
    }

    if (userType === 'manager') {
      return <ManagerPortalApp restaurant={restaurant} user={user} onLogout={handleLogout} />;
    }

    if (userType === 'waiter') {
      return <WaiterDashboard restaurant={restaurant} waiter={user} onLogout={handleLogout} />;
    }

    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>Something went wrong</h2>
        <button onClick={handleLogout}>Go to Login</button>
      </div>
    );
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <Routes>
        <Route path="/menu/:qrCode" element={<PublicMenu />} />
        <Route path="/invoice/:id" element={<PublicInvoicePage />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<AuthenticatedContent />} />
      </Routes>
      {syncBlockMessage && (
        <div style={{
          position: 'fixed', bottom: '30px', left: '50%', transform: 'translateX(-50%)',
          background: '#1e293b', color: 'white', padding: '14px 22px',
          borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
          fontSize: '0.9rem', maxWidth: '420px', textAlign: 'center',
          zIndex: 99999, lineHeight: '1.5',
          borderLeft: '4px solid #f59e0b',
        }}>
          ⚠️ {syncBlockMessage}
        </div>
      )}
    </>
  );
}

export default App;
